// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * QUI VOIT QUOI DANS LE CATALOGUE — le tableau réglable (RQ, 14/09/2026).
 *
 * ── La demande ─────────────────────────────────────────────────────────────
 *
 * « Tout ce qui est dans la fiche doit être tiré d'un champ dans la base.
 *  L'examen doit pouvoir gérer les matières multiples avec différentes
 *  conditions préanalytiques. Le catalogue choisit ce qu'il affiche selon la
 *  personne qui consulte (préleveur, laboratoire, prescripteur). »
 *
 * Deux choses vivaient encore dans le CODE du catalogue :
 *   · quelle rubrique d'un point d'entrée chaque lecteur voit (le préleveur
 *     « À respecter », la réception tout, le confrère les attestations…) ;
 *   · des lignes retirées à l'affichage par expression régulière
 *     (« Identification à deux points », « Volume ≥ … »).
 * Les deux passent en DONNÉES : `field-config.lectureCatalogue`.
 *
 * ── Ce qui reste au code ───────────────────────────────────────────────────
 * La MISE EN PAGE par lecteur (cartes, colonnes, résumé) — c'est une forme,
 * pas un contenu. Tout texte affiché vient d'un champ.
 *
 * ── Tant que rien n'est enregistré ─────────────────────────────────────────
 * `null` → `LECTURE_PAR_DEFAUT`, qui reproduit l'affichage d'avant la bascule.
 * Une rubrique absente d'un réglage enregistré prend aussi son défaut : une
 * rubrique ajoutée plus tard ne disparaît pas des écrans déjà réglés.
 *
 * Module PUR : aucun import d'application. Il vit dans `shared/ui/` pour que le
 * manuel (qui affiche) et Qualité (qui règle) ne puissent pas diverger.
 */

export type LecteurCatalogue = 'preleveur' | 'confrere' | 'medecin' | 'labo';

export const LECTEURS_CATALOGUE: { code: LecteurCatalogue; libelle: string; court: string }[] = [
  { code: 'preleveur', libelle: 'Je prélève (clinique, domicile)', court: 'Préleveur' },
  { code: 'confrere', libelle: 'Je suis un laboratoire', court: 'Laboratoire' },
  { code: 'medecin', libelle: 'Je prescris', court: 'Prescripteur' },
  { code: 'labo', libelle: 'Réception BIO24', court: 'Réception BIO24' },
];
const CODES_LECTEURS = LECTEURS_CATALOGUE.map((l) => l.code);
const TOUS: LecteurCatalogue[] = ['preleveur', 'confrere', 'medecin', 'labo'];

export type RubriqueEntree =
  | 'transport' | 'consigne' | 'consigneExamen' | 'prep' | 'conservation' | 'volume'
  | 'criteres' | 'attestations' | 'refuses' | 'resteAFaire' | 'donneesReception' | 'refus';

/** Les rubriques d'un point d'entrée, dans l'ordre d'affichage. */
export const RUBRIQUES_ENTREE: { code: RubriqueEntree; libelle: string; source: string }[] = [
  { code: 'transport', libelle: 'Mode et délai de transport', source: 'profil ou point d’entrée' },
  { code: 'consigne', libelle: 'Consigne de transport (profil)', source: 'profil de matériel' },
  { code: 'consigneExamen', libelle: 'Consigne propre à l’examen', source: 'point d’entrée' },
  { code: 'prep', libelle: 'Préparation du patient', source: 'point d’entrée' },
  { code: 'conservation', libelle: 'Durée de conservation', source: 'point d’entrée' },
  { code: 'volume', libelle: 'Volume minimal', source: 'point d’entrée' },
  { code: 'criteres', libelle: 'À respecter / critères de réception', source: 'profil de matériel' },
  { code: 'attestations', libelle: 'À nous transmettre avec l’envoi', source: 'profil de matériel' },
  { code: 'refuses', libelle: 'Sera refusé', source: 'profil de matériel' },
  { code: 'resteAFaire', libelle: 'Reste à faire à réception', source: 'profil de matériel' },
  { code: 'donneesReception', libelle: 'À relever à réception', source: 'profil de matériel' },
  { code: 'refus', libelle: 'Matériels refusés pour l’examen (et motif)', source: 'point d’entrée' },
];
const CODES_RUBRIQUES = RUBRIQUES_ENTREE.map((r) => r.code);

export interface RegleCommune {
  /** Identifiant stable (minuscules, chiffres, tirets). */
  id: string;
  texte: string;
  /** Qui la lit — affichée UNE fois, au-dessus des points d'entrée. */
  lecteurs: LecteurCatalogue[];
}

export interface LectureCatalogue {
  visibilite: Record<RubriqueEntree, LecteurCatalogue[]>;
  reglesCommunes: RegleCommune[];
}

/** L'affichage d'avant la bascule (14/09/2026), rubrique par rubrique. */
export const LECTURE_PAR_DEFAUT: LectureCatalogue = {
  visibilite: {
    transport: TOUS,
    consigne: ['preleveur', 'confrere', 'labo'],
    consigneExamen: ['preleveur', 'confrere', 'labo'],
    prep: ['preleveur', 'medecin', 'labo'],
    conservation: ['preleveur', 'confrere', 'labo'],
    volume: ['preleveur', 'confrere', 'labo'],
    criteres: ['preleveur', 'confrere', 'labo'],
    attestations: ['confrere', 'labo'],
    refuses: ['preleveur', 'confrere', 'labo'],
    resteAFaire: ['labo'],
    donneesReception: ['labo'],
    refus: ['confrere', 'medecin', 'labo'],
  },
  reglesCommunes: [
    { id: 'identification', texte: 'Identification à deux points (nom, prénom + date de naissance ou n° de dossier)', lecteurs: ['preleveur', 'labo'] },
  ],
};

export const BORNES_LECTURE = { regles: 30, texte: 400 };

const estLecteur = (x: unknown): x is LecteurCatalogue => CODES_LECTEURS.includes(x as LecteurCatalogue);

/** Le réglage en vigueur : l'enregistré, complété rubrique par rubrique. */
export function lectureEnVigueur(brut: unknown): LectureCatalogue {
  const b = (brut && typeof brut === 'object' && !Array.isArray(brut)) ? brut as Partial<LectureCatalogue> : null;
  if (!b) return LECTURE_PAR_DEFAUT;
  const visibilite = { ...LECTURE_PAR_DEFAUT.visibilite };
  const v = (b.visibilite && typeof b.visibilite === 'object') ? b.visibilite as Record<string, unknown> : {};
  for (const r of CODES_RUBRIQUES) {
    if (Array.isArray(v[r])) visibilite[r] = (v[r] as unknown[]).filter(estLecteur);
  }
  const reglesCommunes = Array.isArray(b.reglesCommunes)
    ? b.reglesCommunes.filter((x) => x && typeof x.texte === 'string').map((x) => ({ id: String(x.id), texte: x.texte, lecteurs: (Array.isArray(x.lecteurs) ? x.lecteurs : []).filter(estLecteur) }))
    : LECTURE_PAR_DEFAUT.reglesCommunes;
  return { visibilite, reglesCommunes };
}

/** Ce lecteur voit-il cette rubrique ? */
export const voitRubrique = (l: LectureCatalogue, r: RubriqueEntree, lecteur: LecteurCatalogue): boolean =>
  (l.visibilite[r] || []).includes(lecteur);

/**
 * Les lecteurs d'un point d'entrée QUI N'EN DÉCLARE PAS (entrée ajoutée avant
 * la bascule, ou déduite) : le préleveur n'a entre les mains que des tubes
 * primaires — une matière séparée suppose une centrifugation, une congelée une
 * chaîne du froid. C'est aussi la valeur que Qualité propose à l'ajout.
 */
export const lecteursParDefautEntree = (etat: string): LecteurCatalogue[] =>
  (etat === 'tube_primaire' ? TOUS : ['confrere', 'medecin', 'labo']);

/** Lecteurs effectifs d'un point d'entrée : les siens, sinon le défaut de son état. */
export function lecteursEntree(m: { lecteurs?: unknown }, etat: string): LecteurCatalogue[] {
  return Array.isArray(m.lecteurs) ? (m.lecteurs as unknown[]).filter(estLecteur) : lecteursParDefautEntree(etat);
}

/** Contrôle d'écriture — même règle au moteur (`erreurLectureCatalogue`). */
export function erreurLecture(brut: unknown): string | null {
  if (!brut || typeof brut !== 'object' || Array.isArray(brut)) return 'Le réglage doit être un objet.';
  const b = brut as Record<string, unknown>;
  const v = b.visibilite;
  if (!v || typeof v !== 'object' || Array.isArray(v)) return '« visibilite » manquante.';
  for (const [r, l] of Object.entries(v as Record<string, unknown>)) {
    if (!CODES_RUBRIQUES.includes(r as RubriqueEntree)) return `Rubrique inconnue : ${r}.`;
    if (!Array.isArray(l) || l.some((x) => !estLecteur(x))) return `« ${r} » : liste de lecteurs invalide.`;
  }
  if (!Array.isArray(b.reglesCommunes)) return '« reglesCommunes » doit être une liste.';
  if (b.reglesCommunes.length > BORNES_LECTURE.regles) return `${BORNES_LECTURE.regles} règles communes au plus.`;
  const ids = new Set<string>();
  for (const x of b.reglesCommunes as Partial<RegleCommune>[]) {
    if (!x || typeof x !== 'object') return 'Règle commune mal formée.';
    if (!/^[a-z0-9-]{1,40}$/.test(String(x.id || ''))) return 'Règle commune : identifiant invalide.';
    if (ids.has(String(x.id))) return `Règle commune en double : ${x.id}.`;
    ids.add(String(x.id));
    if (typeof x.texte !== 'string' || !x.texte.trim()) return 'Une règle commune doit avoir un texte.';
    if (x.texte.length > BORNES_LECTURE.texte) return 'Règle commune trop longue.';
    if (!Array.isArray(x.lecteurs) || x.lecteurs.some((l) => !estLecteur(l))) return `« ${x.texte.slice(0, 30)} » : lecteurs invalides.`;
  }
  return null;
}
